import axios from "axios";
import { notification } from "antd";
import {
  API_BASE_URL,
  IS_TENANT,
  TENANT_ID,
  B2B_BASE_URL,
} from "configs/AppConfig";
import history from "../history";
import {
  PUBLIC_REQUEST_KEY,
  ENTRY_ROUTE,
  TOKEN_PAYLOAD_KEY,
  AUTH_TOKEN_KEY,
  X_TENANT_ID,
  B2B_REQUEST_KEY,
} from "../constants/common.constant";
import { GetLocalStorage, RemoveLocalStorage } from "../utils/common";

// Default request timeout in milliseconds
const DEFAULT_TIMEOUT = 60000;

// Error handler function to standardize error responses
const handleError = (error) => {
  const { data = {}, status, statusText } = error?.response || {};
  return {
    description: data.message || statusText,
    message: data.error || statusText,
    statusCode: data.statusCode || status,
    originalError: error,
  };
};

// Create API client with default configuration
const createApiClient = (baseURL, timeout = DEFAULT_TIMEOUT) => {
  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor
  client.interceptors.request.use(
    async (config) => {
      const jwtToken = GetLocalStorage(AUTH_TOKEN_KEY);

      if (jwtToken) {
        config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
      }

      if (!jwtToken && !config.headers[PUBLIC_REQUEST_KEY]) {
        redirectToLogin();
        return Promise.reject(new Error("Unauthorized access"));
      }

      if (config.headers[B2B_REQUEST_KEY]) {
        config.baseURL = `${B2B_BASE_URL}api/v1`;
      }

      if (IS_TENANT && TENANT_ID) {
        config.headers[X_TENANT_ID] = TENANT_ID;
      }

      return config;
    },
    (error) => {
      notification.error({
        message: "Request Error",
        description:
          error.message || "An error occurred while making the request",
      });
      return Promise.reject(error);
    }
  );

  // Response interceptor
  client.interceptors.response.use(
    (response) => {
      const data = response.data;
      const requestConfig = response.config; // Fix: Get config from response

      if (data.message && requestConfig.showSuccessMessage !== false) {
        notification.success({
          message: "Success",
          description: data.message,
        });
      }

      return data;
    },
    (error) => {
      const errorData = handleError(error);
      const requestConfig = error.response?.config; // Fix: Get config from response

      if ([401, 403].includes(errorData.statusCode)) {
        // const currentUrl = error.response?.config?.url;

        // const ignoreAuthRedirectUrls = [
        //   API_BASE_URL + "/admin/login",
        //   API_BASE_URL + "/auth/refresh",
        // ];

        // if (!ignoreAuthRedirectUrls.includes(currentUrl)) {
        RemoveLocalStorage(AUTH_TOKEN_KEY);

        setTimeout(() => {
          redirectToLogin();
        }, 3000);
        // }
      }

      if (requestConfig?.showErrorNotification !== false) {
        notification.error({
          message: "Error",
          description: errorData.description || "Something went wrong",
        });
      }

      return Promise.reject(errorData);
    }
  );

  return client;
};

// Helper function to redirect to login
const redirectToLogin = () => {
  history.push(ENTRY_ROUTE);
  window.location.reload();
};

// Create default API client
const apiClient = createApiClient(API_BASE_URL);

export const createCustomApiClient = createApiClient;
export default apiClient;
